import { AlertTriangle, Download, Save, X } from 'lucide-solid'
import {
  Component,
  Match,
  Show,
  Switch,
  createEffect,
  createMemo,
  createSignal,
  on,
  onMount,
} from 'solid-js'
import AvatarIcon from '../../shared/AvatarIcon'
import Button from '../../shared/Button'
import FileInput, { FileInputResult, getFileAsString } from '../../shared/FileInput'
import Modal, { RootModal } from '../../shared/Modal'
import TextInput from '../../shared/TextInput'
import { downloadJson, setComponentPageTitle } from '../../shared/util'
import { adminStore, pageStore, settingStore, toastStore, userStore, i18nStore } from '../../store'
import { Pill, TitleCard } from '/web/shared/Card'
import { useNavigate, useSearchParams } from '@solidjs/router'
import { SubscriptionPage } from './SubscriptionPage'
import WorkshopPage from '../Home/WorkshopPage'
import DiscoveryPage from '../Home/DiscoveryPage'
import { ChatList } from '../Character/ChatList'
import { useTabs } from '/web/shared/Tabs'
import { Page } from '/web/Layout'
import { useGoogleReady } from '/web/shared/hooks'
import { startTour } from '/web/tours'
import { createStore } from 'solid-js/store'
import { isLoggedIn } from '/web/store/api'
import { localApi } from '/web/store/data/storage'

export const ProfileModal: Component = () => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const state = userStore((s) => ({ showProfile: s.showProfile }))
  const config = userStore((s) => ({ tiers: s.tiers.filter((t) => t.enabled) }))
  const tabs = useTabs([
    i18n.t.profile, 
    (i18n as any).t.profile_my_creations,
    (i18n as any).t.profile_my_favorites,
    (i18n as any).t.profile_chat_history,
    i18n.t.subscriptions
  ], 0)

  const [search, setSearch] = useSearchParams()

  createEffect(() => {
    const name = search.profile_tab || ''
    if (!name) return

    const index = tabs.tabs().findIndex((t) => t.toLowerCase() === name.toLowerCase())
    if (index > -1) {
      tabs.select(index)
    }

    setSearch({ profile_tab: undefined })
  })

  const [footer, setFooter] = createSignal<any>()

  onMount(() => {
    userStore.getTiers()
  })

  const displayTabs = createMemo(() => {
    if (!config.tiers.length) return false
    return true
  })

  return (
    <Modal
      show={state.showProfile}
      close={() => userStore.modal(false)}
      footer={
        <>
          <Button schema="secondary" onClick={() => userStore.modal(false)}>
            {i18n.t.close}
          </Button>
          {footer()}
        </>
      }
      fixedHeight
      maxWidth="half"
      tabs={displayTabs() ? tabs : undefined}
      ariaLabel={i18n.t.profile}
      ariaDescription={i18n.t.profile_changePasswordDescription}
    >
      <Switch>
        <Match when={!displayTabs()}>
          <ProfilePage footer={setFooter} />
        </Match>
        <Match when={tabs.current() === i18n.t.profile}>
          <ProfilePage footer={setFooter} />
        </Match>
        <Match when={tabs.current() === (i18n as any).t.profile_my_creations}>
          <WorkshopPage />
        </Match>
        <Match when={tabs.current() === (i18n as any).t.profile_my_favorites}>
          <DiscoveryPage />
        </Match>
        <Match when={tabs.current() === (i18n as any).t.profile_chat_history}>
          <ChatList />
        </Match>
        <Match when={tabs.current() === i18n.t.subscriptions}>
          <SubscriptionPage />
        </Match>
      </Switch>
    </Modal>
  )
}

const ProfilePage: Component<{ footer?: (children: any) => void }> = (props) => {
  let formRef: HTMLFormElement
  let googleRef: any

  const i18n = i18nStore((s) => ({ t: s.t }))
  setComponentPageTitle(i18n.t.profile_myProfile)
  const nav = useNavigate()
  const state = userStore((s) => ({ profile: s.profile, user: s.user }))
  const admin = adminStore((s) => ({ impersonating: s.impersonating }))
  const settings = settingStore((s) => ({ config: s.config }))
  const page = pageStore((s) => ({ flags: s.flags }))

  const [handle, setHandle] = createSignal(state.profile?.handle || '')
  const [pass, setPass] = createSignal(false)
  const [del, setDel] = createSignal(false)
  const [avatar, setAvatar] = createSignal<File | undefined>()
  const [importing, setImporting] = createSignal(false)
  const [downloadData, setDownloadData] = createSignal<any>()
  const google = useGoogleReady()

  const canuseGoogle = createMemo(
    () =>
      !!settings.config.serverConfig?.googleClientId &&
      (settings.config.serverConfig?.googleEnabled || page.flags.google)
  )

  const onAvatar = (files: FileInputResult[]) => {
    const [file] = files
    if (!file) return setAvatar()
    setAvatar(() => file.file)
  }

  const submit = () => {
    const payload = { handle: handle(), avatar: avatar() }
    userStore.updateProfile(payload)
    userStore.modal(false)
  }

  const [guestData, setGuestData] = createSignal<any>()

  const importGuestData = () => {
    setGuestData(undefined)
    const warning = (
      <div class="flex flex-col items-center gap-2">
        <FileInput
          fieldName="guest-data-import"
          accept=".txt, text/json, application/json"
          label={i18n.t.profile_guestData}
          onUpdate={async (data) => {
            if (!data?.[0]) return
            const str = await getFileAsString(data[0])
            const json = JSON.parse(str)
            setGuestData(json)
          }}
        />
        <div class="font-bold">!! {i18n.t.char_responseTemplateWarning?.split(':')[0] || ''} !!</div>
        <div>{i18n.t.profile_importGuestDataWarning}</div>
      </div>
    )

    pageStore.openConfirm({
      message: warning,
      onConfirm: async () => {
        if (!guestData()) return
        try {
          setImporting(true)
          await localApi.importGuestData(guestData())
          setImporting(false)
        } catch (ex: any) {
          toastStore.error(i18n.t.profile_importFailed.replace('{message}', ex?.message || ex))
          setImporting(false)
        }
      },
    })
  }

  onMount(() => {
    userStore.getProfile()
    userStore.getConfig()
  })

  createEffect(
    on(
      () => state.profile?.handle,
      (name) => {
        if (name === undefined) return
        setHandle(name)
      }
    )
  )

  const initGoogle = () => {
    const win: any = window
    const api = win.google?.accounts?.id

    if (!api) return

    const enabled = settings.config.serverConfig?.googleEnabled || page.flags.google

    if (settings.config.serverConfig?.googleClientId && enabled) {
      api.initialize({
        client_id: settings.config.serverConfig?.googleClientId,
        callback: (result: any) => {
          userStore.handleGoogleCallback('link', result)
        },
      })

      api.renderButton(googleRef, {
        theme: 'filled_black',
        size: 'large',
        type: 'standard',
        text: 'signin_with',
      })
    }
  }

  createEffect(
    on(
      () => google(),
      () => {
        initGoogle()
      }
    )
  )

  onMount(() => {
    props.footer?.(footer)

    if (state.profile?.handle === 'You') {
      startTour('profile', true)
    }
  })

  const footer = (
    <Button onClick={submit} ariaLabel={i18n.t.profile_updateProfile}>
      <Save aria-hidden="true" />
      <span aria-hidden="true">{i18n.t.profile_updateProfile}</span>
    </Button>
  )

  return (
    <Page>
      <form ref={formRef!} onSubmit={submit} aria-label={i18n.t.profile_myProfile}>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <div class="flex flex-row gap-2">
              <AvatarIcon
                avatarUrl={state.profile?.avatar}
                format={{ size: 'md', corners: 'circle' }}
              />
              <div class="flex items-center">{state.profile?.handle}</div>
            </div>
          </div>

          {/* <TitleCard type="bg" ariaRole="note" ariaLabel={i18n.t.char_impersonate}>
            <div class="flex flex-wrap items-center justify-center">
              {i18n.t.profile_impersonateNote?.split('Impersonate')[0] || ''}
              <div class="inline">
                <Button class="mx-1" size="sm" onClick={() => pageStore.toggleImpersonate(true)}>
                  {i18n.t.char_impersonate}
                </Button>{' '}
              </div>
              {i18n.t.profile_impersonateNote?.split('Impersonate')[1]?.split('Persona')[0] || ''}
              <Pill small class="mx-0.5 px-0.5">
                {i18n.t.persona}
              </Pill>{' '}
              {i18n.t.profile_impersonateNote?.split('Persona')[1] || ''}
            </div>
          </TitleCard> */}

          <Show when={state.user?._id !== 'anon' && canuseGoogle() && !admin.impersonating}>
            <div class="flex justify-center">
              <TitleCard class="flex w-fit flex-col items-center justify-center gap-1" type="hl">
                <Show when={!state.user?.google?.sub}>
                  <div class="flex justify-center text-sm font-bold">
                    {i18n.t.profile_linkGoogle}
                  </div>
                  <div
                    class="flex justify-center"
                    ref={(ref) => {
                      googleRef = ref
                    }}
                    id="g_id_onload"
                    data-context="signin"
                    data-ux_mode="popup"
                    data-login_uri={`${location.origin}/oauth/google`}
                    data-itp_support="true"
                  ></div>
                </Show>

                <Show when={!!state.user?.google?.sub}>
                  <div class="flex justify-center text-sm font-bold">
                    {i18n.t.profile_accountLinkedGoogle}
                  </div>
                  <Show when={state.user?.username !== `google_${state.user?.google?.sub}`}>
                    <div class="flex justify-center">
                      <Button
                        class="justify-center"
                        size="sm"
                        schema="warning"
                        onClick={() => userStore.unlinkGoogleAccount(() => initGoogle())}
                      >
                        {i18n.t.profile_unlinkGoogle}
                      </Button>
                    </div>
                  </Show>
                </Show>
              </TitleCard>
            </div>
          </Show>

          <TextInput
            label={i18n.t.admin_id}
            helperText={i18n.t.profile_userIdHelper}
            fieldName="userid"
            value={state.user?._id}
            disabled
          />

          <TextInput
            label={i18n.t.username}
            helperText={i18n.t.profile_usernameHelper}
            fieldName="username"
            value={state.user?.username}
            disabled
          />

          <TextInput
            label={i18n.t.displayName}
            parentClass="tour-displayname"
            helperText={i18n.t.profile_displayNameHelper}
            fieldName="handle"
            value={handle()}
            onChange={(ev) => setHandle(ev.currentTarget.value)}
          />

          <FileInput
            label={
              <div class="flex items-center gap-2">
                <div>{i18n.t.profile_profileImage}</div>
                <Show when={!!state.profile?.avatar}>
                  <div class="link text-sm" onClick={userStore.removeProfileAvatar}>
                    {i18n.t.profile_removeAvatar}
                  </div>
                </Show>
              </div>
            }
            fieldName="avatar"
            accept="image/jpeg,image/png"
            helperText={i18n.t.profile_fileSizeLimit2MB}
            onUpdate={onAvatar}
          />

          <Show when={isLoggedIn()}>
            <div>
              <Button onClick={() => setPass(true)}>{i18n.t.profile_changePassword}</Button>
            </div>
          </Show>

          <Show when={state.user?._id === 'anon'}>
            <TitleCard title={i18n.t.profile_guestData} center>
              <div class="flex flex-col items-center gap-3">
                <div class="flex justify-center gap-4">
                  <Button
                    schema="success"
                    onClick={async () => {
                      const data = await localApi.getGuestData()
                      setDownloadData(data)
                    }}
                  >
                    {i18n.t.profile_prepareBackup}
                  </Button>
                  <Button disabled={importing()} schema="warning" onClick={importGuestData}>
                    {i18n.t.admin_add}
                  </Button>
                </div>

                <Show
                  when={!!downloadData()}
                  fallback={<span class="text-500">{i18n.t.profile_backupNotReady}</span>}
                >
                  <div class="flex justify-center">
                    <a
                      class="link flex gap-1"
                      onClick={() => {
                        const data = downloadData()
                        downloadJson(data, `guest-data-${Date.now()}.json`)
                      }}
                    >
                      {i18n.t.profile_downloadGuestData} <Download size={16} />
                    </a>
                  </div>
                </Show>
              </div>
            </TitleCard>
          </Show>

          <Show when={state.user?._id !== 'anon'}>
            <div class="flex justify-center gap-4">
              <Show when={admin.impersonating}>
                <Button schema="warning" onClick={() => adminStore.unimpersonate()}>
                  {i18n.t.profile_unimpersonate}
                </Button>
              </Show>
              <Show when={!admin.impersonating}>
                <Button
                  schema="warning"
                  onClick={() => {
                    userStore.logout()
                    nav('/')
                  }}
                >
                  {i18n.t.profile_logout}
                </Button>
              </Show>

              {/* <Button schema="red" onClick={() => setDel(true)}>
                <AlertTriangle /> {i18n.t.profile_deleteAccount} <AlertTriangle />
              </Button> */}

            </div>
          </Show>

          <Show when={!props.footer}>
            <div class="mt-4 flex w-full justify-end">{footer}</div>
          </Show>
        </div>
      </form>
      <PasswordModal show={pass()} close={() => setPass(false)} />
      <DeleteAccountModal show={del()} close={() => setDel(false)} />
    </Page>
  )
}

export default ProfilePage

const PasswordModal: Component<{ show: boolean; close: () => void }> = (props) => {
  const [store, setStore] = createStore({ newPassword: '', confirmPassword: '' })
  const i18n = i18nStore((s) => ({ t: s.t }))

  const save = () => {
    if (store.newPassword !== store.confirmPassword) {
      toastStore.warn(i18n.t.passwordsDoNotMatch)
      return
    }

    if (!store.newPassword) {
      toastStore.warn(i18n.t.profile_updatePassword)
      return
    }

    userStore.changePassword(store.newPassword, props.close)
  }

  return (
    <RootModal
      show={props.show}
      close={props.close}
      title={i18n.t.profile_changePassword}
      ariaLabel={i18n.t.profile_changePassword}
      ariaDescription={i18n.t.profile_changePasswordDescription}
      footer={
        <>
          {' '}
          <Button schema="secondary" onClick={props.close}>
            <X /> {i18n.t.cancel}
          </Button>
          <Button onClick={save}>
            <Save /> {i18n.t.update}
          </Button>
        </>
      }
    >
      <div>
        <form class="flex flex-col gap-2">
          <div class="w-full justify-center">{i18n.t.profile_updatePassword}</div>
          <TextInput
            type="password"
            required
            placeholder={i18n.t.profile_newPassword}
            value={store.newPassword}
            onChange={(ev) => setStore('newPassword', ev.currentTarget.value)}
          />
          <TextInput
            type="password"
            required
            placeholder={i18n.t.profile_confirmPassword}
            value={store.confirmPassword}
            onChange={(ev) => setStore('confirmPassword', ev.currentTarget.value)}
          />
        </form>
      </div>
    </RootModal>
  )
}

const DeleteAccountModal: Component<{ show: boolean; close: () => void }> = (props) => {
  const state = userStore((s) => ({ user: s.user }))
  const [username, setUsername] = createSignal('')
  const i18n = i18nStore((s) => ({ t: s.t }))

  const deleteAccount = () => {
    if (!username()) return
    if (username() !== state.user?.username) return

    props.close()
    userStore.deleteAccount()
  }

  return (
    <RootModal
      title={i18n.t.profile_deleteAccount}
      show={props.show}
      close={props.close}
      footer={
        <>
          <Button schema="secondary" onClick={props.close}>
            {i18n.t.cancel}
          </Button>
        </>
      }
      ariaLabel={i18n.t.profile_deleteAccount}
      ariaDescription={i18n.t.profile_deleteAccountWarning}
    >
      <div class="flex flex-col items-center gap-2">
        <TitleCard type="rose" class="font-bold">
          {i18n.t.profile_deleteAccountWarning}
        </TitleCard>

        <p>{i18n.t.profile_deleteAccountConfirmNote}</p>

        <TextInput
          fieldName="delete-username"
          onChange={(ev) => setUsername(ev.currentTarget.value)}
          placeholder={i18n.t.username}
        />
        <Button disabled={username() !== state.user?.username} schema="red" onClick={deleteAccount}>
          {i18n.t.profile_confirmDeletion}
        </Button>
      </div>
    </RootModal>
  )
}
